import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { ImagePlus, LoaderCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import * as Mutation from '../../api/gql/mutation'
import { fileToDataUrl, imageFileFrom, screenshotProblem } from './feedback-file'

type FeedbackType = 'BUG' | 'FEEDBACK' | 'SUGGESTION' | 'FEATURE'
const TYPES: FeedbackType[] = ['BUG', 'FEEDBACK', 'SUGGESTION', 'FEATURE']
const MAX_MESSAGE = 4000

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Freelo #32277899: a 30-second form — type, message, optional screenshot — delivered to Freelo by the server. */
const FeedbackDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation()
  const [type, setType] = useState<FeedbackType>('FEEDBACK')
  const [message, setMessage] = useState('')
  const [screenshot, setScreenshot] = useState<{ file: File; preview: string } | null>(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [submitFeedback, { loading }] = useMutation(Mutation.SUBMIT_FEEDBACK)

  useEffect(() => {
    if (!open) { setType('FEEDBACK'); setMessage(''); setScreenshot(null); setDragging(false) }
  }, [open])

  const pick = useCallback(async (file: File | null) => {
    if (!file) return
    const problem = screenshotProblem(file)
    if (problem) { toastr.error(t('Common_error'), t(problem)); return }
    setScreenshot({ file, preview: await fileToDataUrl(file) })
  }, [t])

  const onPaste = (e: React.ClipboardEvent) => {
    const file = imageFileFrom(e.clipboardData?.items)
    if (file) { e.preventDefault(); pick(file) }
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    pick(imageFileFrom(e.dataTransfer?.items) ?? e.dataTransfer?.files?.[0] ?? null)
  }

  const canSubmit = message.trim().length > 0 && message.length <= MAX_MESSAGE && !loading

  const submit = async () => {
    if (!canSubmit) return
    try {
      await submitFeedback({
        variables: {
          input: {
            type,
            message: message.trim(),
            pageUrl: window.location.href,
            screenshot: screenshot ? { filename: screenshot.file.name, mimetype: screenshot.file.type, dataBase64: screenshot.preview } : null,
          },
        },
      })
      toastr.success(t('Feedback_sent_title'), t('Feedback_sent_text'))
      onOpenChange(false)
    } catch (e: any) {
      toastr.error(t('Common_error'), e?.message?.replace(/^GraphQL error: /, '') || t('Feedback_error_text'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next) }}>
      <DialogContent className="sm:max-w-lg" onPaste={onPaste}>
        <DialogHeader>
          <DialogTitle>{t('Feedback_title')}</DialogTitle>
          <DialogDescription>{t('Feedback_description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="feedback-type">{t('Feedback_type_label')}</Label>
            <Select value={type} onValueChange={(v) => setType(v as FeedbackType)}>
              <SelectTrigger id="feedback-type" className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPES.map((v) => <SelectItem key={v} value={v}>{t(`Feedback_type_${v.toLowerCase()}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="feedback-message">{t('Feedback_message_label')}</Label>
            <Textarea
              id="feedback-message"
              rows={5}
              autoFocus
              maxLength={MAX_MESSAGE}
              placeholder={t('Feedback_message_placeholder')}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>{t('Feedback_screenshot_label')}</Label>
            {screenshot ? (
              <div className="relative w-fit">
                <img src={screenshot.preview} alt={screenshot.file.name} className="max-h-40 rounded-md border" />
                <Button type="button" variant="secondary" size="icon" className="absolute -right-2 -top-2 h-6 w-6 rounded-full" aria-label={t('Feedback_screenshot_remove')} onClick={() => setScreenshot(null)}>
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className={cn(
                  'flex w-full flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-5 text-sm text-muted-foreground transition-colors hover:bg-accent',
                  dragging && 'border-primary bg-primary/10',
                )}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
              >
                <ImagePlus className="h-5 w-5" />
                <span>{t('Feedback_screenshot_hint')}</span>
              </button>
            )}
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = '' }} />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={!canSubmit}>
            {loading && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Feedback_submit')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FeedbackDialog
